import {
    AutocompleteApi,
    InternalAutocompleteSource
} from "@algolia/autocomplete-core";
import { css } from "@compiled/react";

import { useAnalyticsEvents } from "@atlaskit/analytics-next";
import GenerativeIndicatorIcon from "@atlaskit/icon-lab/core/generative-indicator";

import { wacToken } from "@/libraries/design/foundations";

import { ContentHit } from "./auto-complete-search-bar";

type AutoCompleteSearchAIResultProps = {
    query: string;
    autocomplete: AutocompleteApi<
        ContentHit,
        // eslint-disable-next-line  @typescript-eslint/no-explicit-any
        React.BaseSyntheticEvent<object, any, any>,
        React.MouseEvent<Element, MouseEvent>,
        React.KeyboardEvent<Element>
    >;
    source: InternalAutocompleteSource<ContentHit>;
    item: ContentHit;
};

const aiResultContainerStyle = css({
    "alignItems": "center",
    "borderRadius": "3px",
    "cursor": "pointer",
    "display": "grid",
    "gridTemplateColumns": "auto 1fr",
    "gap": "10px",
    "minHeight": 40,
    "padding": "4px 8px",
    "marginBlockEnd": "8px !important",

    "&:first-child": {
        marginBlockStart: "0px"
    },

    "&:last-child": {
        marginBlockEnd: "0px"
    },

    "&[aria-selected=true]": {
        visibility: "visible",
        backgroundColor: wacToken("color.neutral.light200")
    },

    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "& svg path": {
        fill: wacToken("color.neutral.light600")
    },

    "&:hover": {
        backgroundColor: wacToken("color.neutral.light200")
    }
});

const queryStyle = css({
    fontWeight: 600
});

const AutoCompleteSearchAIResult = ({
    query,
    autocomplete,
    source,
    item
}: AutoCompleteSearchAIResultProps) => {
    const { createAnalyticsEvent } = useAnalyticsEvents();
    const href = `/search?q=${query}`;

    return (
        <li
            className="aa-Item"
            {...autocomplete.getItemProps({ item, source })}
        >
            <a
                css={aiResultContainerStyle}
                href={href}
                onClick={(event) => {
                    event.preventDefault();
                    createAnalyticsEvent({
                        eventName: "clickAutocompleteSearchResult",
                        eventType: "ui",
                        action: "clicked",
                        actionSubject: "linkButton",
                        actionSubjectId: "globalNavSearchResults",
                        source: "globalNav",
                        attributes: {
                            product: "search",
                            queryId: item.queryID,
                            objectId: item.objectID,
                            linkUrl: href,
                            pageUrl: window.location.href,
                            pagePath: window.location.pathname,
                            rank: 0
                        }
                    }).fire();
                    window.location.href = href;
                }}
            >
                <GenerativeIndicatorIcon label="Search AI Result" />
                <div css={queryStyle}>{query}</div>
            </a>
        </li>
    );
};

export default AutoCompleteSearchAIResult;
